import React, { useEffect, useState } from 'react';
import { Search, X } from 'react-feather';

import { useProductSearch } from '@/Order/hooks/useProductSearch';
import { useOrderCreateStore } from '@/Order/store/orderCreateStore';
import {
  AddProductModalProps,
  SelectedProduct,
} from '@/Order/types/orderTypes';

const AddProductModal: React.FC<AddProductModalProps> = ({ onClose }) => {
  const {
    productList,
    isLoading,
    hasResults,
    searchKeyword,
    handleSearchChange,
  } = useProductSearch();

  const {
    selectedProducts,
    addProduct,
    removeProduct,
    isProductSelected,
    currencyCode,
    getProductPrice,
    getCurrencySymbol,
    formatPrice,
  } = useOrderCreateStore();

  const [localSearchKeyword, setLocalSearchKeyword] = useState<string>('');

  // 디바운싱 처리
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearchChange(localSearchKeyword);
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearchKeyword, handleSearchChange]);

  // handleCheckboxChange 함수에서 SelectedProduct 생성 부분 수정
  const handleCheckboxChange = (productItem: any, checked: boolean) => {
    if (checked) {
      const selectedProduct: SelectedProduct = {
        productUuid: productItem.productUuid,
        optionUuid: productItem.optionUuid,
        thumbnailUrl: productItem.thumbnailUrl,
        title: productItem.title,
        optionNameKey1: productItem.optionNameKey1,
        optionNameValue1: productItem.optionNameValue1,
        optionNameKey2: productItem.optionNameKey2,
        optionNameValue2: productItem.optionNameValue2,
        quantity: productItem.minimumOrderQuantity,
        productPriceList: productItem.productPriceList,
        minimumOrderQuantity: productItem.minimumOrderQuantity,
      };
      addProduct(selectedProduct);
    } else {
      removeProduct(productItem.optionUuid);
    }
  };

  // 우측 목록에서 상품 제거
  const handleRemoveFromList = (optionUuid: string) => {
    removeProduct(optionUuid);
  };

  // Save 버튼 핸들러
  const handleSave = () => {
    onClose();
  };

  // Cancel 버튼 핸들러 (저장하지 않음)
  const handleCancel = () => {
    onClose();
  };

  // 상품 가격 가져오기
  const getProductPriceForDisplay = (productPriceList: any[]) => {
    const price = getProductPrice(productPriceList, currencyCode);
    return price > 0 ? formatPrice(price) : '-';
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-product-modal-title"
    >
      <div className="mx-4 w-full max-w-5xl rounded-lg bg-white">
        <div className="flex items-center justify-between border-b border-[#BBBBBBB] p-6">
          <h3 className="text-lg font-medium" id="add-product-modal-title">
            Add Product
          </h3>
          <div className="flex gap-2">
            <button
              type="button"
              className="h-7 cursor-pointer rounded border border-[#EEEEEE] bg-white px-3 py-1 text-sm font-medium text-[#666666] hover:bg-gray-100 hover:text-gray-800"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="h-7 cursor-pointer rounded bg-[#3E3E3E] px-3 py-1 text-sm font-medium text-white hover:bg-opacity-80"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>

        <div className="flex max-h-[600px]">
          {/* Left Section */}
          <div className="flex flex-1 flex-col">
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={localSearchKeyword}
                onChange={(e) => setLocalSearchKeyword(e.target.value)}
                className="h-10 w-full border-[#D9D9D9] bg-white pl-10 pr-4 placeholder-gray-400 focus:border-gray-400 focus:outline-none"
              />
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 transform text-[#666666]"
              />
            </div>

            <div className="flex-1 overflow-hidden rounded-b border border-[#D9D9D9]">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#EFEFEF]">
                    <th className="w-12 border-b border-[#D9D9D9] px-3 py-1.5 text-sm font-medium text-[#707070]" />
                    <th className="w-16 border-b border-[#D9D9D9] px-3 py-1.5 text-sm font-medium text-[#707070]" />
                    <th className="border-b border-[#D9D9D9] px-3 py-1.5 text-left text-sm font-medium text-[#707070]">
                      Product name
                    </th>
                    <th className="w-32 border-b border-[#D9D9D9] px-3 py-1.5 text-left text-sm font-medium text-[#707070]">
                      Option name
                    </th>
                    <th className="w-32 border-b border-[#D9D9D9] px-3 py-1.5 text-left text-sm font-medium text-[#707070]">
                      Option value
                    </th>
                    <th className="w-24 border-b border-[#D9D9D9] px-3 py-1.5 text-left text-sm font-medium text-[#707070]">
                      Price ({getCurrencySymbol()})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        검색 중...
                      </td>
                    </tr>
                  )}

                  {!isLoading &&
                    !hasResults &&
                    searchKeyword.trim().length > 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-3 py-8 text-center text-sm text-gray-500"
                        >
                          검색 결과가 없습니다.
                        </td>
                      </tr>
                    )}

                  {!isLoading && searchKeyword.trim().length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-3 py-8 text-center text-sm text-gray-500"
                      >
                        검색어를 입력해주세요.
                      </td>
                    </tr>
                  )}

                  {!isLoading &&
                    hasResults &&
                    productList.map((product) => (
                      <tr
                        key={product.optionUuid}
                        className="border-b border-[#BBBBBBB] hover:bg-gray-50"
                      >
                        <td className="px-3 py-2 text-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[#8F689A]"
                            checked={isProductSelected(product.optionUuid)}
                            onChange={(e) =>
                              handleCheckboxChange(product, e.target.checked)
                            }
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <img
                            src={product.thumbnailUrl || '/img/logo_b2b.svg'}
                            alt="product"
                            className="h-10 w-10 rounded object-contain"
                          />
                        </td>
                        <td className="px-3 py-2 text-xs text-[#1D1D1D]">
                          <div className="line-clamp-2 text-ellipsis">
                            {product.title}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-[#1D1D1D]">
                          <div className="line-clamp-2 text-ellipsis">
                            {product.optionNameKey1}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center text-xs text-[#1D1D1D]">
                          {product.optionNameValue1}
                        </td>
                        <td className="px-3 py-2 text-xs text-[#1D1D1D]">
                          {getProductPriceForDisplay(product.productPriceList)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex w-80 flex-col border-l border-[#BBBBBBB] bg-[#F1F1F1]">
            <div className="border-b border-[#D9D9D9] p-4">
              <h4 className="text-sm font-semibold text-[#1D1D1D]">
                Product List ({selectedProducts.length})
              </h4>
            </div>

            {/* Selected Products List */}
            <div className="flex-1 overflow-y-auto">
              {selectedProducts.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  선택된 상품이 없습니다.
                </div>
              ) : (
                selectedProducts.map((product) => (
                  <div
                    key={product.optionUuid}
                    className="flex gap-3 border-b border-[#D9D9D9] p-4"
                  >
                    <img
                      src={product.thumbnailUrl || '/img/logo_b2b.svg'}
                      alt="product"
                      className="h-11 w-11 flex-shrink-0 rounded object-contain"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-start justify-between gap-2">
                        <p className="line-clamp-2 text-xs font-semibold leading-[1.4] text-[#1D1D1D]">
                          {product.title}
                        </p>
                        <button
                          type="button"
                          className="flex-shrink-0 rounded p-1 hover:bg-gray-200"
                          onClick={() =>
                            handleRemoveFromList(product.optionUuid)
                          }
                        >
                          <X size={16} className="text-[#999999]" />
                        </button>
                      </div>
                      <div className="mb-3 text-xs leading-[1.4] text-[#444444]">
                        {product.optionNameKey1 && product.optionNameValue1 && (
                          <span>
                            {product.optionNameKey1}: {product.optionNameValue1}
                          </span>
                        )}
                        {product.optionNameKey2 && product.optionNameValue2 && (
                          <span>
                            , {product.optionNameKey2}:{' '}
                            {product.optionNameValue2}
                          </span>
                        )}
                      </div>
                      <p className="text-right text-xs font-semibold text-[#1D1D1D]">
                        {getCurrencySymbol()}
                        {getProductPriceForDisplay(product.productPriceList)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProductModal;
